(() => {
  const ROOT_SELECTOR = '[data-sa-package-protection]';
  const TOGGLE_SELECTOR = '[data-sa-package-protection-toggle]';
  const EVENT_SOURCE = 'cart-package-protection';

  const log = (message) => {
    if (window.devLog) {
      window.devLog('CartProtection', message);
    }
  };

  const getRequestConfig = (type = 'json') => {
    const fallbackConfig =
      type === 'json'
        ? {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
          }
        : {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
            },
          };

    const config = typeof fetchConfig === 'function' ? fetchConfig(type) : fallbackConfig;
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    return config;
  };

  const parseJsonResponse = async (response) => {
    const responseText = await response.text();

    if (!responseText) {
      return {};
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      log(`Failed to parse cart response: ${error.message}`);
      return {};
    }
  };

  const getSectionInnerHTML = (html, selector) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const target = documentFragment.querySelector(selector);
    return target ? target.innerHTML : '';
  };

  const getPageSectionsToRender = () => {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
        selector: '.js-contents',
      });
    }

    if (mainCartFooter?.dataset.id) {
      sections.push({
        id: 'main-cart-footer',
        section: mainCartFooter.dataset.id,
        selector: '.js-contents',
      });
    }

    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });

    return sections;
  };

  const getDrawerSectionsToRender = () => {
    const cartDrawer = document.querySelector('cart-drawer');

    if (!cartDrawer?.getSectionsToRender) {
      return [];
    }

    return cartDrawer.getSectionsToRender().map((section) => ({
      id: section.id,
      section: section.id,
      selector: section.selector || '.shopify-section',
    }));
  };

  const getSectionsToRender = (context) => {
    return context === 'drawer' ? getDrawerSectionsToRender() : getPageSectionsToRender();
  };

  const renderPageSections = (sectionsHtml) => {
    getPageSectionsToRender().forEach((section) => {
      const targetRoot = document.getElementById(section.id);
      const sectionHtml = sectionsHtml?.[section.section];

      if (!targetRoot || !sectionHtml) {
        return;
      }

      const target = targetRoot.querySelector(section.selector) || targetRoot;
      const nextHtml = getSectionInnerHTML(sectionHtml, section.selector);

      if (nextHtml) {
        target.innerHTML = nextHtml;
      }
    });
  };

  const applyCartResponse = (container, response) => {
    const context = container.dataset.context || 'page';

    if (context === 'drawer') {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

      if (cartDrawer?.renderContents && response?.sections) {
        cartDrawer.renderContents(response);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }
    } else if (response?.sections) {
      renderPageSections(response.sections);
    } else {
      window.location.reload();
      return;
    }

    if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: EVENT_SOURCE,
        cartData: response,
        variantId: container.dataset.variantId,
      });
    }
  };

  const setLoadingState = (container, isLoading) => {
    const toggle = container.querySelector(TOGGLE_SELECTOR);
    if (!toggle) {
      return;
    }

    if (!toggle.dataset.baseDisabled) {
      toggle.dataset.baseDisabled = toggle.disabled ? 'true' : 'false';
    }

    container.classList.toggle('is-updating', isLoading);
    container.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    toggle.disabled = isLoading ? true : toggle.dataset.baseDisabled === 'true';
  };

  const containerNeedsCleanup = (container) => {
    const toggle = container.querySelector(TOGGLE_SELECTOR);

    return !!toggle &&
      toggle.checked &&
      container.dataset.line &&
      container.dataset.hasEligibleItems !== 'true';
  };

  const addProtectionToCart = async (container) => {
    const variantId = container.dataset.variantId;
    if (!variantId) {
      throw new Error('Missing package protection variant ID');
    }

    const sectionsToRender = getSectionsToRender(container.dataset.context || 'page');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('properties[_sa_package_protection]', 'true');

    if (sectionsToRender.length > 0) {
      formData.append(
        'sections',
        sectionsToRender.map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
    }

    const config = getRequestConfig('javascript');
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
    const parsedResponse = await parseJsonResponse(response);

    if (!response.ok || parsedResponse.status) {
      throw new Error(parsedResponse.description || parsedResponse.message || 'Failed to add package protection');
    }

    return parsedResponse;
  };

  const removeProtectionFromCart = async (container) => {
    const line = container.dataset.line;
    if (!line) {
      throw new Error('Missing package protection cart line');
    }

    const sectionsToRender = getSectionsToRender(container.dataset.context || 'page');
    const config = getRequestConfig('json');
    config.body = JSON.stringify({
      line: Number(line),
      quantity: 0,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', config);
    const parsedResponse = await parseJsonResponse(response);

    if (!response.ok || parsedResponse.errors) {
      throw new Error(parsedResponse.errors || parsedResponse.description || 'Failed to remove package protection');
    }

    return parsedResponse;
  };

  const bindGlobalToggleHandler = () => {
    const root = document.documentElement;

    if (!root) {
      return;
    }

    if (window.initOnce && !window.initOnce(root, 'cart-package-protection')) {
      return;
    }

    root.addEventListener('change', async (event) => {
      const toggle = event.target.closest(TOGGLE_SELECTOR);
      if (!toggle) {
        return;
      }

      const container = toggle.closest(ROOT_SELECTOR);
      if (!container || container.classList.contains('is-updating')) {
        return;
      }

      const nextCheckedState = toggle.checked;

      setLoadingState(container, true);

      try {
        const response = nextCheckedState
          ? await addProtectionToCart(container)
          : await removeProtectionFromCart(container);

        applyCartResponse(container, response);
      } catch (error) {
        toggle.checked = !nextCheckedState;
        log(error.message);
        console.error(error);
      } finally {
        setLoadingState(container, false);
      }
    });
  };

  const cleanupOrphanedProtection = async () => {
    const containers = Array.from(document.querySelectorAll(ROOT_SELECTOR));

    for (const container of containers) {
      if (!containerNeedsCleanup(container) || container.dataset.cleanupInProgress === 'true') {
        continue;
      }

      container.dataset.cleanupInProgress = 'true';
      setLoadingState(container, true);

      try {
        const response = await removeProtectionFromCart(container);
        applyCartResponse(container, response);
      } catch (error) {
        log(`Failed to clean up orphaned package protection: ${error.message}`);
        console.error(error);
      } finally {
        container.dataset.cleanupInProgress = 'false';
        setLoadingState(container, false);
      }

      return;
    }
  };

  bindGlobalToggleHandler();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupOrphanedProtection, { once: true });
  } else {
    cleanupOrphanedProtection();
  }

  if (window.subscribe && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === EVENT_SOURCE) {
        return;
      }

      cleanupOrphanedProtection();
    });
  }
})();